import type React from 'react';
import { useEffect, useRef } from 'react';
import fromSiteBg from '../assets/bernd-dittrich-gFud7-XmQvk-unsplash.jpg';
import { TextAnimate } from './TextAnimate';
import { useRAFScroll } from '../hooks/useRAFScroll';
import { mobileQuery } from '../utils/mediaQueries';
import './FromSite.css';

const PARALLAX_SPEEDS = { heading: 0.15, subheading: 0.15, grid: 0.15 } as const;
const PARALLAX_SPEEDS_MOBILE = { heading: 0.10, subheading: 0.10, grid: 0.10 } as const;

interface Capability {
  title: string;
  desc: string;
}

const CAPABILITIES: readonly Capability[] = [
  { title: 'Origination', desc: 'Identifying high-quality sites across strategic transmission corridors' },
  { title: 'Structuring', desc: 'Aligning stakeholders, capital, and grid connection agreements' },
  { title: 'Delivery', desc: 'Managing build, HV integration, and data centre construction' },
  { title: 'Commissioning', desc: 'Bringing generation, storage, and compute assets online' },
];

export default function FromSite(): React.JSX.Element {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const subheadingRef = useRef<HTMLParagraphElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const sectionTopRef = useRef<number>(0);

  useEffect(() => {
    const update = () => {
      if (sectionRef.current)
        sectionTopRef.current = sectionRef.current.getBoundingClientRect().top + window.scrollY;
    };
    update();
    window.addEventListener('resize', update, { passive: true });
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('fromsite--zooming');
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useRAFScroll(() => {
    const el = sectionRef.current;
    if (!el) return;
    const vh = window.innerHeight;
    const delta = vh / 2 - (sectionTopRef.current + el.offsetHeight / 2 - window.scrollY);
    const speeds = mobileQuery.matches ? PARALLAX_SPEEDS_MOBILE : PARALLAX_SPEEDS;

    if (headingRef.current)
      headingRef.current.style.transform = `translateY(${delta * -speeds.heading}px)`;
    if (subheadingRef.current)
      subheadingRef.current.style.transform = `translateY(${delta * -speeds.subheading}px)`;
    if (gridRef.current)
      gridRef.current.style.transform = `translateY(${delta * -speeds.grid}px)`;
  });

  return (
    <section
      className="fromsite"
      id="from-site"
      ref={sectionRef}
      aria-labelledby="fromsite-heading"
    >
      <div
        className="fromsite__bg"
        style={{ backgroundImage: `url(${fromSiteBg})` }}
        aria-hidden="true"
      />
      <div className="fromsite__overlay" aria-hidden="true" />
      <div className="fromsite__inner">
        <h2 id="fromsite-heading" className="fromsite__heading" ref={headingRef}>
          From Site to System,
          <br />
          Fully Delivered
        </h2>
        <p className="fromsite__subheading" ref={subheadingRef}>
          We combine technical execution with commercial discipline to move projects —
          from grid-connected BESS and renewable generation to rack-ready data centre capacity —
          from concept to operation with speed and certainty.
        </p>
        <div className="fromsite__grid" ref={gridRef} role="list" aria-label="Capabilities">
          {CAPABILITIES.map((cap, i) => (
            <div key={cap.title} className={`fromsite__cell fromsite__cell--${i}`} role="listitem">
              <TextAnimate
                animation="fadeIn"
                by="word"
                stagger={0.1}
                each={0.4}
                delay={i * 0.15}
                as="h3"
                className="fromsite__cell-title"
              >
                {cap.title}
              </TextAnimate>
              <TextAnimate
                animation="fadeIn"
                by="word"
                stagger={0.05}
                each={0.4}
                delay={i * 0.15 + 0.2}
                as="p"
                className="fromsite__cell-desc"
              >
                {cap.desc}
              </TextAnimate>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
